import { DEFAULT_SKILLS } from './definitions';
import type { AnySkill, SkillsOverlay, SkillSurface } from './types';

// v2: `kind` became required on AnySkill. A v1 overlay's edited/added skills
// predate it, and they are stored whole — an edited skill replaces the code
// default outright — so a stale entry would come back kind-less and drop out
// of both prompt sections while still showing as enabled in the modal.
// Discarding v1 loses only local skill tweaks, which are a dev convenience.
const STORAGE_KEY = 'toolbox-skills-overlay-v2';
const LEGACY_STORAGE_KEYS = ['toolbox-skills-overlay-v1'];

const EMPTY_OVERLAY: SkillsOverlay = { disabled: [], edited: {}, added: [] };

function discardLegacyOverlays(): void {
    for (const key of LEGACY_STORAGE_KEYS) {
        try {
            window.localStorage.removeItem(key);
        } catch {
            // Storage may be unavailable; the stale key is inert either way.
        }
    }
}

/**
 * `kind` is required at compile time, but overlay skills are JSON off
 * localStorage and can be hand-edited, so backfill it rather than trust the
 * cast. A kind-less skill would match neither prompt section and render a
 * blank badge, while still reading as enabled.
 */
function withKind(skill: AnySkill): AnySkill {
    return skill.kind === 'video' || skill.kind === 'howto' || skill.kind === 'codeo'
        ? skill
        : { ...skill, kind: 'video' };
}

function readOverlay(): SkillsOverlay {
    discardLegacyOverlays();
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return { ...EMPTY_OVERLAY };
        }
        const parsed = JSON.parse(raw) as Partial<SkillsOverlay>;
        return {
            disabled: parsed.disabled ?? [],
            edited: Object.fromEntries(
                Object.entries(parsed.edited ?? {}).map(([name, skill]) => [name, withKind(skill)]),
            ),
            added: (parsed.added ?? []).map(withKind),
        };
    } catch {
        return { ...EMPTY_OVERLAY };
    }
}

function writeOverlay(overlay: SkillsOverlay): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overlay));
    } catch {
        // Storage may be unavailable (private mode) or over quota; skill edits
        // are a dev-only convenience, so silently drop the write rather than
        // throwing out of a click handler.
    }
}

export function getOverlay(): SkillsOverlay {
    return readOverlay();
}

export function getActiveSkills(surface?: SkillSurface): AnySkill[] {
    const overlay = readOverlay();
    const defaultNames = new Set<string>(DEFAULT_SKILLS.map((s) => s.name));

    const merged: AnySkill[] = DEFAULT_SKILLS.map((skill) => overlay.edited[skill.name] ?? skill);

    for (const added of overlay.added) {
        if (!defaultNames.has(added.name)) {
            merged.push(overlay.edited[added.name] ?? added);
        }
    }

    const disabled = new Set(overlay.disabled);
    return merged
        .filter((skill) => !disabled.has(skill.name))
        .filter((skill) => !surface || !skill.surfaces || skill.surfaces.includes(surface));
}

export function setSkillEnabled(name: string, enabled: boolean): void {
    const overlay = readOverlay();
    const disabled = new Set(overlay.disabled);
    if (enabled) {
        disabled.delete(name);
    } else {
        disabled.add(name);
    }
    writeOverlay({ ...overlay, disabled: Array.from(disabled) });
}

export function upsertSkill(skill: AnySkill): void {
    const overlay = readOverlay();
    const isDefault = DEFAULT_SKILLS.some((s) => s.name === skill.name);
    const existingAdded = overlay.added.some((s) => s.name === skill.name);

    if (isDefault || existingAdded) {
        writeOverlay({
            ...overlay,
            edited: { ...overlay.edited, [skill.name]: skill },
        });
        return;
    }

    writeOverlay({ ...overlay, added: [...overlay.added, skill] });
}

export function removeAddedSkill(name: string): void {
    const overlay = readOverlay();
    const edited = { ...overlay.edited };
    delete edited[name];
    writeOverlay({
        ...overlay,
        added: overlay.added.filter((s) => s.name !== name),
        edited,
        disabled: overlay.disabled.filter((n) => n !== name),
    });
}

export function resetSkills(): void {
    window.localStorage.removeItem(STORAGE_KEY);
}
